import React from 'react';
import { Modal } from '../../components/ui';
import { normaliseText } from '../../utils/helpers';
import { MODULES, MODULE_LABELS, ACTIONS, REPORT_BITS } from './useUsers';

const ACTION_LABELS = { '': 'View', Add: 'Add', Edit: 'Edit', Delete: 'Delete' };

// Desktop add-member / permissions dialog: a purposeful 640px width, the new
// user's fields on one line (each box as wide as what goes in it), designation /
// side / admin on the next, then the permission matrix as a compact grid with
// an "All" tick per module. Enter in a text box saves. Logic: useUsers.
export default function UserFormDesktop({ vm }) {
  const { isAdmin, modal, setModal, editing, saving, err, form, available, setF, setPerm, save, event, needEvent, isMarriage } = vm;
  const onEnter = (e) => { if (e.key === 'Enter' && !saving && !needEvent) { e.preventDefault(); save(); } };

  const bitsOf = (m) => ACTIONS.map(a => `Can${m}${a}`);
  const allOn = (m) => bitsOf(m).every(b => !!form.perms[b]);
  const setAll = (m, on) => bitsOf(m).forEach(b => setPerm(b, on));

  const permMatrix = (
    <div style={{ marginTop: 4 }}>
      <div className="pp-head-line">
        <span className="step-label">Permissions</span>
        <span className="muted" style={{ fontSize: 12 }}>
          {form.IsAdmin ? 'Event Admins have full access — per-module permissions are ignored.' : 'Tick what this user may do per module.'}
        </span>
      </div>
      <div className={'pp-perm' + (form.IsAdmin ? ' off' : '')}>
        <div className="pp-perm-row head">
          <span>Module</span>
          {ACTIONS.map(a => <span key={a}>{ACTION_LABELS[a] ?? a}</span>)}
          <span>All</span>
        </div>
        {MODULES.map(m => (
          <div key={m} className="pp-perm-row">
            <span className="pp-perm-mod">{MODULE_LABELS[m] || m}</span>
            {ACTIONS.map(a => {
              const bit = `Can${m}${a}`;
              return (
                <label key={a} className="pp-perm-cell" title={`${MODULE_LABELS[m] || m} · ${ACTION_LABELS[a] ?? a}`}>
                  <input type="checkbox" checked={!!form.perms[bit]} onChange={e => setPerm(bit, e.target.checked)} />
                </label>
              );
            })}
            <span className="pp-perm-all">
              <button type="button" className="pp-link" onClick={() => setAll(m, !allOn(m))}>{allOn(m) ? 'None' : 'All'}</button>
            </span>
          </div>
        ))}
      </div>
      {REPORT_BITS.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          <strong>Reports:&nbsp;</strong>
          {REPORT_BITS.map(([bit, lbl]) => (
            <label key={bit} style={{ marginRight: 14, opacity: form.IsAdmin ? 0.5 : 1 }}>
              <input type="checkbox" checked={!!form.perms[bit]} disabled={form.IsAdmin}
                onChange={e => setPerm(bit, e.target.checked)} /> {lbl}
            </label>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      className="pp-user-modal"
      title={modal === 'add' ? 'Add Member' : `Permissions · ${editing?.Name || ''}`}
      onClose={() => setModal(null)}
      footer={<>
        <button className="btn light" onClick={() => setModal(null)}>Cancel</button>
        <button className="btn primary" disabled={saving || needEvent} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
      </>}
    >
      {/* Permissions are per-event — make the scope explicit. */}
      <div className="pp-scope">
        {needEvent
          ? 'Pick an event in the top bar first — permissions are set per event.'
          : <>Permissions apply to event: <strong>{event?.EventName}</strong></>}
      </div>
      {modal === 'add' && (
        <>
          <div className="pp-seg pp-mode">
            <button type="button" className={form.addMode === 'existing' ? 'on' : ''} onClick={() => setF('addMode', 'existing')}>Existing user</button>
            <button type="button" className={form.addMode === 'new' ? 'on' : ''} onClick={() => setF('addMode', 'new')}>New user</button>
          </div>
          {form.addMode === 'existing' ? (
            <label className="field">Select user
              <select autoFocus value={form.UserId ?? ''} onChange={(e) => setF('UserId', e.target.value)}>
                <option value="">- select -</option>
                {available.map(u => (
                  <option key={u.UserId} value={u.UserId}>{`${u.FullName || u.Name} · ${u.MobileNumber || ''}`}</option>
                ))}
              </select>
            </label>
          ) : (
            <div className="pp-row pp-user-new">
              <label className="field">Name *
                <input autoFocus value={form.Name ?? ''} onChange={(e) => setF('Name', normaliseText(e.target.value))} onKeyDown={onEnter} />
              </label>
              <label className="field">Surname
                <input value={form.Surname ?? ''} onChange={(e) => setF('Surname', normaliseText(e.target.value))} onKeyDown={onEnter} />
              </label>
              <label className="field">Mobile *
                <input inputMode="numeric" placeholder="10 digits" value={form.MobileNumber ?? ''}
                  onChange={(e) => setF('MobileNumber', e.target.value.replace(/\D/g, '').slice(0, 10))} onKeyDown={onEnter} />
              </label>
              <label className="field">Password *
                <input value={form.Password ?? ''} onChange={(e) => setF('Password', e.target.value)} onKeyDown={onEnter} />
              </label>
            </div>
          )}
        </>
      )}

      {/* Admin powers — promote to Event Admin + the permission matrix — are
          admin-only. A non-admin adds plain members (who can also add users). */}
      {isAdmin ? (
        <>
          <div className="pp-row pp-user-admin">
            <label className="field">Designation
              <input value={form.Designation ?? ''} onChange={(e) => setF('Designation', normaliseText(e.target.value))} onKeyDown={onEnter} />
            </label>
            {/* Two-sided event: a member invites for exactly one side and every
                guest they add gets it. Admins work across both (Side stays 'Common'). */}
            {isMarriage && !form.IsAdmin ? (
              <div className="pp-seg-field">Invites for side *
                <div className="pp-seg">
                  {['Groom', 'Bride'].map(s => (
                    <button key={s} type="button" className={form.Side === s ? 'on' : ''} onClick={() => setF('Side', s)}>{s}</button>
                  ))}
                </div>
              </div>
            ) : <span />}
            <label className={'pp-check' + (form.IsAdmin ? ' on' : '')}>
              <input type="checkbox" checked={!!form.IsAdmin} onChange={(e) => setF('IsAdmin', e.target.checked)} />
              Event Admin (full access)
            </label>
          </div>
          {permMatrix}
        </>
      ) : (
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          This person will be added as a <strong>member</strong> — they can sign in, add their own guests, and add more users.
        </div>
      )}
      {err && <div className="pp-err">{err}</div>}
    </Modal>
  );
}
